<template>
  <div>
    <form ref="form">
      <b-field :label="$t('legalStreetName')">
        <b-input v-model="formData.legalStreetName" required />
      </b-field>
      <b-field :label="$t('legalZipCode')">
        <b-input v-model="formData.legalZipCode" required />
      </b-field>
      <b-field :label="$t('legalCity')">
        <b-input v-model="formData.legalCity" required />
      </b-field>
    </form>
    <b-field :label="$t('legalCountry')">
      <b-autocomplete
        v-model="countryInput"
        :data="avCountriesFiltered"
        icon="earth"
        field="name"
        :open-on-focus="true"
        :placeholder="$t('selectACountry')"
        :loading="isLoading"
        autocomplete="off"
        @select="
          (option) => {
            formData.legalCountry = '';
            option ? (formData.legalCountry = option.alpha2) : '';
          }
        "
      />
    </b-field>
    <b-message type="is-primary">
      {{ $t("SingleTranslatorExplained") }}
    </b-message>
    <b-field>
      <b-checkbox v-model="formData.independent">
        {{ $t("SingleTranslator") }}
      </b-checkbox>
    </b-field>
    <b-message class="mt-3" type="is-primary">
      {{ $t("departmentsExplained") }}
    </b-message>
    <b-dropdown v-model="formData.departments" position="is-top-right" multiple aria-role="list" class="mb-3">
      <button slot="trigger" class="button is-primary" type="button">
        <span>{{ $t("specialFields") }}</span>
        <b-icon icon="menu-down" />
      </button>

      <b-dropdown-item v-for="(department, index) in departments" :key="index" :value="department" aria-role="listitem">
        {{ $t(department) }}
      </b-dropdown-item>
    </b-dropdown>
    <b-taglist class="mb-4">
      <b-tag
        v-for="(department, key) in formData.departments"
        :key="key"
        closable
        type="is-primary"
        size="is-medium"
        @close="formData.departments.splice(key, 1)"
      >
        {{ $t(department) }}
      </b-tag>
    </b-taglist>
    <b-dropdown v-model="formData.associations" position="is-top-right" multiple aria-role="list" class="mb-3">
      <button slot="trigger" class="button is-primary" type="button">
        <span>{{ $t("assocs") }}</span>
        <b-icon icon="menu-down" />
      </button>

      <b-dropdown-item v-for="(a, index) in assocs" :key="index" :value="a" aria-role="listitem">
        {{ a }}
      </b-dropdown-item>
    </b-dropdown>
    <b-taglist class="mb-4">
      <b-tag
        v-for="(a, key) in formData.associations"
        :key="key"
        closable
        type="is-primary"
        size="is-medium"
        @close="formData.associations.splice(key, 1)"
      >
        {{ a }}
      </b-tag>
    </b-taglist>
    <b-message class="is-primary">
      {{ $t("languagesTranslatorExplained") }}
    </b-message>
    <div v-for="lang in formData.langComb" :key="lang._sortId" class="box">
      <b-field grouped group-multiline>
        <LanguagePicker
          v-model="lang.lang1"
          class="control min-width-300"
          :blacklist="[lang.lang2]"
          :change-local="false"
          :loading="false"
          :disabled="false"
        />
        <b-tooltip type="is-primary" class="control" :label="$t(lang.mono ? 'monodirectional' : 'bidirectional')">
          <b-button
            :icon-left="lang.mono ? 'arrow-right-bold' : 'arrow-left-right-bold'"
            @click="lang.mono = !lang.mono"
          />
        </b-tooltip>
        <LanguagePicker
          v-model="lang.lang2"
          class="control min-width-300"
          :blacklist="[lang.lang1]"
          :change-local="false"
          :loading="false"
          :disabled="false"
        />
        <b-button type="is-danger" icon-left="delete" @click="removeLangCombo(lang._sortId)" />
      </b-field>
      <div class="mt-2 ml-4">
        <b-field>
          <b-switch v-model="lang.consecutive" @click.native="lang.consecutive ? (lang.simultaneous = true) : ''">
            {{ $t("consecutive") }}
          </b-switch>
        </b-field>
        <b-field>
          <b-switch v-model="lang.simultaneous" @click.native="lang.simultaneous ? (lang.consecutive = true) : ''">
            {{ $t("simultaneous") }}
          </b-switch>
        </b-field>
      </div>
    </div>

    <b-field group-multiline grouped>
      <b-button class="is-primary control" icon-left="plus" @click="addLangCombo">
        {{ $t("addLangCombo") }}
      </b-button>
    </b-field>
    <b-field class="mt-4" :grouped="true" :group-multiline="true">
      <b-button
        class="control"
        type="is-primary"
        icon-left="content-save-outline"
        :loading="isLoading"
        :disabled="!valid"
        @click="saveSettings"
      >
        {{ $t("saveSettings") }}
      </b-button>
    </b-field>
  </div>
</template>

<script>
import LanguagePicker from "@/components/LanguagePicker";
import { v4 } from "uuid";
import isoCountries from "@/iso-countries";
import { functions } from "@/firebase";
import cloneDeep from "clone-deep";

export default {
  name: "TranslatorSettings",
  components: {
    LanguagePicker,
  },
  props: {
    translator: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      assocs: [
        "VKD",
        "BDÜ",
        "AIIC",
        "ADÜ Nord",
        "ATICOM",
        "VVDÜ",
        "VBDÜ",
        "VGDÜ",
        "UNIVERSITAS AUSTRIA",
        "VZGDÜ",
        "ASTTI",
        "SFT",
        "AITI",
      ],
      departments: [
        "specialFieldSport",
        "specialFieldSoccer",
        "specialFieldEconomy",
        "specialFieldEnv",
        "specialFieldTech",
        "specialFieldPolitics",
        "specialFieldPharma",
        "specialFieldMedicine",
        "specialFieldMachines",
        "specialFieldIT",
        "specialFieldFinance",
        "specialFieldEnergy",
        "specialFieldDigital",
      ],
      isLoading: false,
      countryInput: "",
      valid: true,
      formData: {
        legalCity: "",
        legalStreetName: "",
        legalZipCode: "",
        legalCountry: "",
        langComb: [],
        independent: false,
        departments: [],
        associations: [],
      },
    };
  },
  computed: {
    avCountries() {
      return isoCountries;
    },
    avCountriesFiltered() {
      return this.avCountries.filter((option) => {
        return (
          option.name.toString().toLowerCase().indexOf(this.countryInput.toLowerCase()) >= 0 ||
          (option.alpha3 && option.alpha3.toString().toLowerCase().indexOf(this.countryInput.toLowerCase()) >= 0)
        );
      });
    },
  },
  watch: {
    translator: {
      handler() {
        console.log(this.translator);
        this.formData = cloneDeep(this.translator);
        this.formData.langComb = [];
        for (let lang of this.translator.langComb) {
          this.addLangCombo(lang);
        }

        let country = this.avCountries.filter((option) => {
          return (
            option.alpha2 &&
            option.alpha2.toString().toLowerCase().indexOf(this.formData.legalCountry.toLowerCase()) >= 0
          );
        });
        this.countryInput = country[0].name;
      },
    },
    formData: {
      deep: true,
      handler() {
        this.valid = this.$refs.form.checkValidity() || true;
      },
    },
  },
  methods: {
    async saveSettings() {
      try {
        this.isLoading = true;
        await functions.httpsCallable("SetTranslatorData")({
          legalStreetName: this.formData.legalStreetName,
          legalZipCode: this.formData.legalZipCode,
          legalCountry: this.formData.legalCountry,
          legalCity: this.formData.legalCity,
          independent: this.formData.independent,
          langComb: this.formData.langComb,
          departments: this.formData.departments,
          associations: this.formData.associations,
        });
        this.$buefy.toast.open({
          type: "is-success",
          message: this.$t("settingsSaved"),
        });
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: error,
        });
      } finally {
        this.isLoading = false;
      }
    },
    addLangCombo(lang = {}) {
      this.formData.langComb.push({
        lang1: lang.lang1 || "",
        lang2: lang.lang2 || "",
        mono: typeof lang.mono === "undefined" ? true : lang.mono,
        simultaneous: typeof lang.simultaneous === "undefined" ? false : lang.simultaneous,
        consecutive: typeof lang.consecutive === "undefined" ? true : lang.consecutive,
        _sortId: v4(),
      });
    },
    removeLangCombo(key) {
      this.$delete(
        this.formData.langComb,
        this.formData.langComb.findIndex((y) => y._sortId == key),
      );
      if (this.formData.langComb.length == 0) {
        this.addLangCombo();
      }
    },
  },
  firestore() {
    return {};
  },
};
</script>

<style></style>
